import { useEffect, useState } from "react";
import { Link2, Loader2, Unlink, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { TIPOS_VINCULO, TIPO_VINCULO_LABEL, SEMAFORO_COBERTURA, fmtMoney } from "@/lib/fideicomisos";
import { toast } from "sonner";

export function CoberturaBadge({ semaforo, cobertura }) {
  if (!semaforo) return <span className="text-xs text-slate-400">N/A</span>;
  const m = SEMAFORO_COBERTURA[semaforo] || {};
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {cobertura != null ? `${Number(cobertura).toFixed(2)}x` : ""} {m.label}
    </span>
  );
}

export function CreditosCoberturaTab({ fid, reload, puedeEscribir }) {
  const cob = fid.cobertura || {};
  const vinculos = fid.vinculos || [];
  const terminal = ["extinguido", "revertido"].includes(fid.estado);
  const [abrir, setAbrir] = useState(false);
  const [q, setQ] = useState("");
  const [creditos, setCreditos] = useState([]);
  const [sel, setSel] = useState(null);
  const [tipo, setTipo] = useState("garantia_principal");
  const [prelacion, setPrelacion] = useState("1");
  const [aforo, setAforo] = useState("");
  const [busy, setBusy] = useState(false);
  const [liberar, setLiberar] = useState(null); // vinculo
  const [motivo, setMotivo] = useState("");
  const [confirmar, setConfirmar] = useState(false);

  useEffect(() => {
    if (!abrir) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/fideicomisos/creditos/buscar", { params: { q } });
        setCreditos(data);
      } catch (e) { console.debug(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [abrir, q]);

  const vincular = async () => {
    setBusy(true);
    try {
      const body = { credito_id: sel.id, tipo_vinculo: tipo };
      if (prelacion) body.orden_prelacion = prelacion;
      if (aforo) body.aforo_pactado = aforo;
      await api.post(`/fideicomisos/${fid.id}/creditos`, body);
      toast.success(`Crédito ${sel.numero_credito} vinculado. Garantía espejo "Fiduciaria" creada.`);
      setAbrir(false); setSel(null); setQ(""); setAforo(""); setPrelacion("1"); setTipo("garantia_principal");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const ejecutarLiberacion = async () => {
    setBusy(true);
    try {
      await api.post(`/fideicomisos/${fid.id}/creditos/${liberar.id}/liberar`, { motivo, confirmar });
      toast.success("Vínculo liberado. Garantía espejo → Liberada.");
      setLiberar(null); setMotivo(""); setConfirmar(false);
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const liquidado = liberar && (liberar.credito?.estatus === "Liquidado");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4" data-testid="fid-cobertura-panel">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Patrimonio vigente</p>
          <p className="mt-1 text-lg font-bold text-slate-900" data-testid="fid-cob-patrimonio">{fmtMoney(cob.patrimonio_vigente)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Saldo garantizado</p>
          <p className="mt-1 text-lg font-bold text-slate-900" data-testid="fid-cob-saldo">{fmtMoney(cob.saldo_garantizado)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Aforo pactado</p>
          <p className="mt-1 text-lg font-bold text-slate-900">{Number(cob.aforo_objetivo || 1).toFixed(2)}x</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Cobertura</p>
          <div className="mt-1.5" data-testid="fid-cob-semaforo"><CoberturaBadge semaforo={cob.semaforo} cobertura={cob.cobertura} /></div>
        </div>
      </div>
      {cob.semaforo === "rojo" && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="fid-cob-alerta-roja">
          Cobertura insuficiente — el fideicomisario puede exigir bienes adicionales o pago anticipado (LGTOC, Sección Segunda). Se generó alerta para el Oficial de Cumplimiento.
        </p>
      )}
      {cob.todos_liquidados && (
        <p className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="fid-cob-fin-realizado">
          Todos los créditos garantizados están liquidados: candidato a extinción (Art. 392 fr. I LGTOC). La extinción es decisión humana desde el expediente.
        </p>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <p className="text-sm font-semibold text-slate-800">Créditos garantizados (referencia a cartera — sin recaptura)</p>
          {puedeEscribir && !terminal && (
            <Button data-testid="fid-btn-vincular" size="sm" onClick={() => setAbrir(true)}>
              <Link2 className="mr-1.5 h-4 w-4" /> Vincular crédito
            </Button>
          )}
        </div>
        {vinculos.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400">Sin créditos vinculados.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5">Crédito</th><th className="px-4 py-2.5">Cliente</th>
                <th className="px-4 py-2.5">Estatus</th><th className="px-4 py-2.5">Saldo insoluto</th>
                <th className="px-4 py-2.5">Vínculo</th><th className="px-4 py-2.5">Prelación</th>
                <th className="px-4 py-2.5">Aforo</th><th className="px-4 py-2.5">Estado</th><th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {vinculos.map((v) => (
                <tr key={v.id} className="border-b border-slate-50" data-testid={`fid-vinculo-${v.id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{v.credito?.numero_credito || v.credito_id}</td>
                  <td className="px-4 py-2.5 text-slate-600">{v.credito?.cliente || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{v.credito?.estatus || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700">{fmtMoney(v.credito?.saldo_insoluto)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{TIPO_VINCULO_LABEL[v.tipo_vinculo] || v.tipo_vinculo}</td>
                  <td className="px-4 py-2.5 text-slate-600">{v.orden_prelacion || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{v.aforo_pactado ? `${v.aforo_pactado}x` : "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${v.estado_vinculo === "activo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {v.estado_vinculo}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {puedeEscribir && !terminal && v.estado_vinculo === "activo" && (
                      <Button data-testid={`fid-vinculo-liberar-${v.id}`} size="sm" variant="outline"
                        onClick={() => { setLiberar(v); setMotivo(""); setConfirmar(false); }}>
                        <Unlink className="mr-1 h-3 w-3" /> Liberar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Vincular crédito */}
      <Dialog open={abrir} onOpenChange={(v) => { if (!v) { setAbrir(false); setSel(null); setQ(""); } }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="fid-dialog-vincular">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Scale className="h-5 w-5" /> Vincular crédito al fideicomiso</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Crédito de la cartera *</label>
              {sel ? (
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-800">{sel.numero_credito} — {sel.cliente} <span className="text-xs text-slate-500">({sel.estatus} · {fmtMoney(sel.saldo_insoluto)})</span></span>
                  <Button size="sm" variant="ghost" onClick={() => setSel(null)}>Cambiar</Button>
                </div>
              ) : (
                <>
                  <Input data-testid="fid-vincular-buscar" placeholder="Folio (CR-...) o cliente…" value={q} onChange={(e) => setQ(e.target.value)} />
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-100">
                    {creditos.map((c) => (
                      <button key={c.id} data-testid={`fid-vincular-credito-${c.id}`}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => setSel(c)}>
                        <span className="text-slate-800">{c.numero_credito} — {c.cliente}</span>
                        <span className="text-xs text-slate-400">
                          {c.estatus} · {fmtMoney(c.saldo_insoluto)}{c.ya_garantia_principal ? " · YA PRINCIPAL" : ""}
                        </span>
                      </button>
                    ))}
                    {creditos.length === 0 && <p className="p-3 text-center text-xs text-slate-400">Sin créditos activos que coincidan</p>}
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="mb-1 block text-xs font-medium text-slate-600">Tipo de vínculo</label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger data-testid="fid-vincular-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_VINCULO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Prelación</label>
                <Input data-testid="fid-vincular-prelacion" type="number" min="1" value={prelacion} onChange={(e) => setPrelacion(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Aforo pactado (ej. 1.5)</label>
                <Input data-testid="fid-vincular-aforo" type="number" step="0.1" min="0.1" value={aforo} onChange={(e) => setAforo(e.target.value)} />
              </div>
            </div>
            <p className="rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-500">
              Al vincular se crea un registro espejo en Garantías (tipo "Fiduciaria") por el flujo normal, con el patrimonio vigente como valor.
            </p>
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-2">
            <Button variant="outline" onClick={() => setAbrir(false)}>Cancelar</Button>
            <Button data-testid="fid-vincular-confirmar" onClick={vincular} disabled={busy || !sel}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Vincular crédito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Liberar vínculo */}
      <Dialog open={!!liberar} onOpenChange={(v) => { if (!v) setLiberar(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-md" data-testid="fid-dialog-liberar">
          <DialogHeader><DialogTitle className="font-heading">Liberar vínculo — {liberar?.credito?.numero_credito}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {liquidado ? (
              <p className="rounded-md bg-emerald-50 p-3 text-xs text-emerald-700">El crédito está Liquidado: la liberación procede sin doble confirmación.</p>
            ) : (
              <>
                <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-700">
                  El crédito NO está liquidado ({liberar?.credito?.estatus}). La liberación requiere doble confirmación y motivo obligatorio (queda en bitácora).
                </p>
                <Textarea data-testid="fid-liberar-motivo" rows={2} placeholder="Motivo de la liberación *" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <Checkbox data-testid="fid-liberar-confirmar-check" checked={confirmar} onCheckedChange={(v) => setConfirmar(!!v)} />
                  Confirmo la liberación anticipada de la garantía fiduciaria.
                </label>
              </>
            )}
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-2">
            <Button variant="outline" onClick={() => setLiberar(null)}>Cancelar</Button>
            <Button data-testid="fid-liberar-confirmar" onClick={ejecutarLiberacion}
              disabled={busy || (!liquidado && (!motivo.trim() || !confirmar))}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Liberar vínculo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
